import { useCallback, useRef } from 'react';
import type { MediaFile } from '../components/media-input';
import type { Logger, PromptMedia, UploadedFile } from '../types/types';
type UseFileUploadProps = {
  uploadMediaFiles?: (mediaFiles: PromptMedia[]) => Promise<UploadedFile[] | Error>;
  logger?: Logger;
};
export const useCachedFileUpload = ({
  uploadMediaFiles,
  logger
}: UseFileUploadProps) => {
  const uploadedFilesCache = useRef<Map<string, UploadedFile>>(new Map());
  const getFileKey = useCallback((file: File) => {
    return `${file.name}-${file.size}-${file.lastModified}`;
  }, []);
  const getUploadedFile = (file: File) => {
    const fileKey = getFileKey(file);
    return uploadedFilesCache.current.get(fileKey);
  };
  const uploadFiles = useCallback(async (mediaFiles: MediaFile[]): Promise<UploadedFile[] | Error> => {
    try {
      if (!uploadMediaFiles) {
        return new Error('Upload function is not available');
      }
      if (!mediaFiles || !Array.isArray(mediaFiles)) {
        return new Error('Invalid mediaFiles parameter');
      }
      if (mediaFiles.length === 0) {
        return [];
      }
      const cachedFiles: UploadedFile[] = [];
      const filesToUpload: MediaFile[] = [];
      for (const mediaFile of mediaFiles) {
        if (!mediaFile || !mediaFile.file) {
          return new Error('Invalid media file: missing file property');
        }
        const fileKey = getFileKey(mediaFile.file);
        const cachedFile = uploadedFilesCache.current.get(fileKey);
        if (cachedFile) {
          cachedFiles.push(cachedFile);
        } else {
          filesToUpload.push(mediaFile);
        }
      }
      let newlyUploadedFiles: UploadedFile[] | Error = [];
      if (filesToUpload.length > 0) {
        if (logger) {
          logger.info(`Uploading new media files: ${filesToUpload.length} new, ${cachedFiles.length} cached, ${mediaFiles.length} total`);
        }
        const promptMedia = filesToUpload.map(mediaFile => ({
          file: mediaFile.file,
          type: mediaFile.type
        }));
        newlyUploadedFiles = await uploadMediaFiles(promptMedia);
        if (newlyUploadedFiles instanceof Error) {
          return newlyUploadedFiles;
        }
        if (!newlyUploadedFiles || !Array.isArray(newlyUploadedFiles)) {
          // Validate the response
          return new Error('Upload function did not return a valid array');
        }
        if (newlyUploadedFiles.length !== filesToUpload.length) {
          return new Error(`Upload mismatch: expected ${filesToUpload.length} files, got ${newlyUploadedFiles.length}`);
        }
        for (const [index, mediaFile] of filesToUpload.entries()) {
          const fileKey = getFileKey(mediaFile.file);
          const uploadedFile = newlyUploadedFiles[index];
          if (!uploadedFile) {
            return new Error(`Missing upload result for file at index ${index}`);
          }
          uploadedFilesCache.current.set(fileKey, uploadedFile);
        }
      } else if (logger) {
        logger.info(`Using cached media files: ${cachedFiles.length} files`);
      }
      const uploadedFiles: UploadedFile[] = [];
      for (const mediaFile of mediaFiles) {
        const fileKey = getFileKey(mediaFile.file);
        const cachedFile = uploadedFilesCache.current.get(fileKey);
        if (!cachedFile) {
          return new Error(`File not found in cache: ${mediaFile.file.name}`);
        }
        uploadedFiles.push(cachedFile);
      }
      if (logger) {
        logger.info(`Upload complete: ${uploadedFiles.length} files ready`);
      }
      return uploadedFiles;
    } catch (error) {
      if (logger) {
        logger.error('Failed to upload files', {
          error
        });
      }
      return new Error(error instanceof Error ? error.message : 'Failed to upload files');
    }
  }, [uploadMediaFiles, logger, getFileKey]);
  return {
    uploadFiles,
    getUploadedFile
  };
};